import { AppToastOutlet, useAppToastRef } from './app-toast-provider';
import { checkDependencyCycle, dependencyCycleMessage, findDependencyCycle } from '../general/dependency-cycles';
import { useUnsavedActions, useUnsavedDraft } from '../general/unsaved-changes-context';
import { createContext, useCallback, useEffect, useRef, useState } from "react";
import { useBookmarkSelection } from '../general/bookmark-selection';
import { CourseDto, CourseUserDto, ListCourseDto, MessageDto, ModuleDto, UserDto } from "../general/dtos";
import { Api } from "../general/api";
import { Toast } from "primereact/toast";
import { useApi } from "./api-provider";

export interface CourseManagementDataContextValue {
    toastRef: React.RefObject<Toast | null>;

    allCourses: ListCourseDto[];
    allCoursesLoading: boolean;
    allCoursesError: string | null;
    loadAllCourses: () => Promise<void>;

    allModules: ModuleDto[];
    allModulesLoading: boolean;
    allModulesError: string | null;
    loadAllModules: () => Promise<void>;

    allUsers: UserDto[];
    allUsersLoading: boolean;
    allUsersError: string | null;
    loadAllUsers: () => Promise<void>;

    // Selected course (full DTO)
    selectedCourseKey: string | null;
    selectedCourseFull: CourseDto | null;
    selectedCourseLoading: boolean;
    selectedCourseError: string | null;

    // Actions
    mode: "view" | "edit" | "create";
    selectCourse: (courseKey: string | null) => void;
    startEditCourse: (courseKey: string) => void;
    startCreateCourse: () => void;
    initNewCourse: () => void;

    // new update functions
    updateCourseField: (field: keyof CourseDto, value: any) => void;
    updateCoursePrerequisites: (courseKeys: string[]) => void;
    updateCourseModules: (moduleKeys: string[]) => void;
    updateModulePrerequisites: (moduleKey: string, prereqKeys: string[], toggledKey?: string, newOptional?: boolean) => void;
    updateCourseUsers: (subs: string[]) => void;
    addUsersToCourse: (course_key: string, subs: string[]) => void;
    removeUserFromCourse: (course_key: string, sub: string) => void;
    validateCourse: () => string[] | null;
    saveCourse: () => Promise<boolean>;
    registerCourseDraft: (collect: () => Partial<CourseDto> | null) => () => void;
    originalCourseFull: CourseDto | null;
    saveErrorLocation: { section: string; field?: string | null; request: number } | null;
    deleteCourse: (course_key: string) => void;
    cancelEditing: () => void;
}

export const CourseManagementDataContext = createContext<CourseManagementDataContextValue | null>(null);

export function CourseManagementDataProvider({ children }: { children: React.ReactNode }) {
  const toastRef = useAppToastRef();
  const { requestAction, withoutWarning, afterSave } = useUnsavedActions();
  const api: Api = useApi();
  const courseDraft = useRef<(() => Partial<CourseDto> | null) | null>(null);
  const courseSaveInFlight = useRef(false);
  const [saveErrorLocation, setSaveErrorLocation] = useState<{ section: string; field?: string | null; request: number } | null>(null);
  const registerCourseDraft = useCallback((collect: () => Partial<CourseDto> | null) => {
    courseDraft.current = collect;
    return () => { if (courseDraft.current === collect) courseDraft.current = null; };
  }, []);

  // courses
  const [allCourses, setAllCourses] = useState<ListCourseDto[]>([]);
  const [allCoursesLoading, setAllCoursesLoading] = useState<boolean>(false);
  const [allCoursesError, setAllCoursesError] = useState<string | null>(null);
  const coursesInFlight = useRef<Promise<void> | null>(null);

  // modules
  const [allModules, setAllModules] = useState<ModuleDto[]>([]);
  const [allModulesLoading, setAllModulesLoading] = useState<boolean>(false);
  const [allModulesError, setAllModulesError] = useState<string | null>(null);
  const modulesInFlight = useRef<Promise<void> | null>(null);

  // users
  const [allUsers, setAllUsers] = useState<UserDto[]>([]);
  const [allUsersLoading, setAllUsersLoading] = useState<boolean>(false);
  const [allUsersError, setAllUsersError] = useState<string | null>(null);
  const usersInFlight = useRef<Promise<void> | null>(null);

  const [folderBookmark] = useBookmarkSelection('folder');
  const folderId = folderBookmark && /^[1-9][0-9]*$/.test(folderBookmark) && Number.isSafeInteger(Number(folderBookmark)) ? Number(folderBookmark) : null;

  // selected course
  const [selectedCourseKey, setSelectedCourseKey] = useBookmarkSelection('course');
  const courseLoadGeneration = useRef(0);
  const courseEditRequest = useRef<string | null>(null);
  const invalidateCourseLoad = useCallback(() => { courseLoadGeneration.current++; }, []);
  const [selectedCourseFull, setSelectedCourseFull] = useState<CourseDto | null>(null);
  const selectedCourseRef = useRef(selectedCourseFull);
  selectedCourseRef.current = selectedCourseFull;
  const [originalCourseFull, setOriginalCourseFull] = useState<CourseDto | null>(null);
  const [selectedCourseLoading, setSelectedCourseLoading] = useState(false);
  const [selectedCourseError, setSelectedCourseError] = useState<string | null>(null);

  // mode
  const [mode, setMode] = useState<"view" | "edit" | "create">("view");

  const loadAllCourses = async (): Promise<void> => {
    if (coursesInFlight.current) {
        return coursesInFlight.current;
    }

    setAllCoursesLoading(true);
    setAllCoursesError(null);

    const promise = api.searchCourses(0, 10000, '', '', '', '')
    .then((data) => {
      setAllCourses(data.data);
      setAllCoursesLoading(false);
      coursesInFlight.current = null;
    })
    .catch((error) => {
      const message: string = error.response?.data?.message ?? 'Load error';
      setAllCoursesError(message);
      setAllCoursesLoading(false);
      coursesInFlight.current = null;
    });

    coursesInFlight.current = promise;
    return promise;
  };

  const loadAllModules = async (): Promise<void> => {
    if (modulesInFlight.current) {
        return modulesInFlight.current;
    }

    setAllModulesLoading(true);
    setAllModulesError(null);

    const promise = api.getModules(0, 10000, '', '', '', '')
    .then((data) => {
      setAllModules(data.data);
      setAllModulesLoading(false);
      modulesInFlight.current = null;
    })
    .catch((error) => {
      const message: string = error.response?.data?.message ?? 'Load error';
      setAllModulesError(message);
      setAllModulesLoading(false);
      modulesInFlight.current = null;
    });

    modulesInFlight.current = promise;
    return promise;
  };

  const loadAllUsers = async (): Promise<void> => {
    if (usersInFlight.current) {
        return usersInFlight.current;
    }

    setAllUsersLoading(true);
    setAllUsersError(null);

    const promise = api.getUsers(0, 10000, '', '', '', '')
    .then((data) => {
      setAllUsers(data.data);
      setAllUsersLoading(false);
      usersInFlight.current = null;
    })
    .catch((error) => {
      const message: string = error.response?.data?.message ?? 'Load error';
      setAllUsersError(message);
      setAllUsersLoading(false);
      usersInFlight.current = null;
    });

    usersInFlight.current = promise;
    return promise;
  };

  // Load full course DTO by key
  const loadCourse = useCallback(async (courseKey: string) => {
    setSaveErrorLocation(null);
    const generation = ++courseLoadGeneration.current;
    setSelectedCourseLoading(true);
    setSelectedCourseError(null);

    try {
      const dto: CourseDto | null = await api.getCourse(courseKey);
      if (generation !== courseLoadGeneration.current) return;
      if (!dto) throw new Error('Course not found.');
      const resolvedDto: CourseDto = {
            ...dto,
            modules: dto!.modules.map(m => ({
            ...m,
            prerequisites: m.prerequisites ?? []
            })),
            course_prerequisites: dto!.course_prerequisites ?? [],
            users: dto!.users ?? []
        } as CourseDto;
      guard.markSaved(resolvedDto);
      setSelectedCourseFull(resolvedDto);
      setOriginalCourseFull(resolvedDto);
    } catch (error: any) {
        if (generation !== courseLoadGeneration.current) return;
        const message: string = error.response?.data?.message ?? 'Failed to load course';
        setSelectedCourseError(message);
        setSelectedCourseFull(null);
    } finally {
        if (generation === courseLoadGeneration.current) setSelectedCourseLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  useEffect(() => {
    if (selectedCourseKey && api) {
      setMode(courseEditRequest.current === selectedCourseKey ? 'edit' : 'view');
      courseEditRequest.current = null;
      setSelectedCourseFull(null);
      void loadCourse(selectedCourseKey);
    } else if (!selectedCourseKey) {
      courseLoadGeneration.current++;
      if (mode !== 'create') setSelectedCourseFull(null);
      setSelectedCourseError(null);
      setSelectedCourseLoading(false);
    }
    return invalidateCourseLoad;
    // Mode is an editing concern, not part of a read-only bookmark.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourseKey, api, loadCourse, invalidateCourseLoad]);

  // Called when user selects a course in the left panel
  const selectCourse = useCallback((courseKey: string | null) => {
    setMode("view");
    setSelectedCourseKey(courseKey);
  }, [setSelectedCourseKey]);

  // Initialize a new empty course (create mode)
  const initNewCourse = useCallback(() => {
    setSaveErrorLocation(null);
    setSelectedCourseKey(null);

    const empty: CourseDto = {
        course_key: "",
        folder_id: folderId,
        name: "New Course",
        description: "",
        cert_description: null,
        cert_prefix: null,
        certificate_enabled: true,
        price: null,
        modules: [],
        course_prerequisites: [],
        users: [],
        promotions: [],
        loyalty_discounts: [],
        answer_comparisons: [],
    };

    setSelectedCourseFull(empty);
    setSelectedCourseLoading(false);
    setSelectedCourseError(null);
  }, [setSelectedCourseKey, folderId]);

  const updateCourseField = useCallback((field: keyof CourseDto, value: any) => {
    setSelectedCourseFull(prev => prev ? { ...prev, [field]: value } : prev);
  }, []);

  const updateCoursePrerequisites = useCallback((courseKeys: string[]) => {
    setSelectedCourseFull(prev => {
        if (!prev) return prev;

        return {
            ...prev,
            course_prerequisites: courseKeys.map(key => ({
                course_key: key,
                is_optional: false   // default; can be edited later
            }))
        };
    });
  }, []);

  const updateCourseModules = useCallback((moduleKeys: string[]) => {
    setSelectedCourseFull(prev => {
        if (!prev) return prev;

        return {
            ...prev,
            modules: moduleKeys.map((key, index) => ({
                module_key: key,
                position: index + 1,
                prerequisites: []   // dependencies edited in Modules tab
            }))
        };
    });
  }, []);

  const updateModulePrerequisites = useCallback(
    (
        moduleKey: string,
        prereqKeys: string[],
        toggledKey?: string,
        newOptional?: boolean
    ) => {
        setSelectedCourseFull(prev => {
            if (!prev) return prev;

            // Build updated modules list
            const updatedModules = prev.modules.map(m => {
                if (m.module_key !== moduleKey) return m;

                let newPrereqs = prereqKeys.map(key => {
                    const existing = m.prerequisites.find(p => p.module_key === key);
                    return existing ?? { module_key: key, is_optional: false };
                });

                if (toggledKey) {
                    newPrereqs = newPrereqs.map(p =>
                        p.module_key === toggledKey
                            ? { ...p, is_optional: newOptional ?? false }
                            : p
                    );
                }

                return { ...m, prerequisites: newPrereqs };
            });

            const nodes = new Map(updatedModules.map(module => [module.module_key, {
                key: module.module_key,
                name: allModules.find(item => item.module_key === module.module_key)?.name || module.module_key,
                prerequisites: module.prerequisites.map(item => item.module_key),
            }]));
            const cycle = findDependencyCycle(nodes, moduleKey);
            if (cycle) {
                toastRef.current?.show({ severity: 'error', summary: 'Invalid dependency',
                    detail: dependencyCycleMessage(nodes, cycle), life: 10000 });
                return prev;
            }
            return { ...prev, modules: updatedModules };
        });
    },
    [allModules, toastRef]
  );

  const updateCourseUsers = useCallback((subs: string[]) => {
    setSelectedCourseFull(prev => {
        if (!prev) return prev;

        return {
            ...prev,
            users: subs.map(sub => ({
                sub,
                assigned_at: new Date().toISOString()
            }))
        };
    });
  }, []);

  const startEditCourse = useCallback((courseKey: string) => {
    courseEditRequest.current = courseKey;
    setMode("edit");
    setSelectedCourseKey(courseKey);
    if (courseKey === selectedCourseKey) { courseEditRequest.current = null; void loadCourse(courseKey); }
  }, [loadCourse, selectedCourseKey, setSelectedCourseKey]);

  const startCreateCourse = useCallback(() => {
    setOriginalCourseFull(selectedCourseRef.current);
    setMode("create");
    initNewCourse();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initNewCourse, selectedCourseFull]);

  useEffect(() => {
    loadAllCourses();
    loadAllModules();
    loadAllUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addUsersToCourse = useCallback(
    (course_key: string, subs: string[]) => {
        setSelectedCourseFull(prev => {
            if (!prev) return prev;
            if (prev.course_key !== course_key) return prev;

            // Build a set of existing subs to avoid duplicates
            const existing = new Set(prev.users.map(u => u.sub));

            const newUsers: CourseUserDto[] = subs
                .filter(sub => !existing.has(sub))
                .map(sub => ({
                    sub,
                    assigned_at: new Date().toISOString()
                }));

            return {
                ...prev,
                users: [...prev.users, ...newUsers]
            };
        });
    },
    []
  );

  const removeUserFromCourse = useCallback(
    (course_key: string, sub: string) => {
        setSelectedCourseFull(prev => {
            if (!prev) return prev;
            if (prev.course_key !== course_key) return prev;

            return {
                ...prev,
                users: prev.users.filter(u => u.sub !== sub)
            };
        });
    },
    []
  );

  const validateCourse = useCallback((): string[] | null => {
    if (!selectedCourseFull) return ["No course loaded"];

    const errors: string[] = [];
    const c = selectedCourseFull;

    if (c.price == null) {
        errors.push("Course price is required.");
    } else if (!Number.isFinite(c.price) || c.price <= 0) {
        errors.push("Course price must be greater than zero.");
    }

    // Name required
    if (!c.name || c.name.trim().length === 0) {
        errors.push("Course name is required.");
    }

    // Description optional but recommended
    if (!c.description || c.description.trim().length === 0) {
        errors.push("Course description is required.");
    }

    // Must have at least one module
    if (!c.modules || c.modules.length === 0) {
        errors.push("A course must contain at least one module.");
    }

    // No duplicate modules (should never happen, but safe to check)
    const moduleKeys = c.modules.map(m => m.module_key);
    const duplicates = moduleKeys.filter((k, i) => moduleKeys.indexOf(k) !== i);
    if (duplicates.length > 0) {
        errors.push("Duplicate modules detected: " + duplicates.join(", "));
    }

    // Course prerequisites must not include itself
    const prereqKeys = c.course_prerequisites.map(p => p.course_key);
    if (prereqKeys.includes(c.course_key)) {
        errors.push("A course cannot depend on itself.");
    }

    const nodes = new Map(c.modules.map(module => [module.module_key, {
        key: module.module_key,
        name: allModules.find(item => item.module_key === module.module_key)?.name || module.module_key,
        prerequisites: module.prerequisites.map(item => item.module_key),
    }]));
    for (const module of c.modules) {
        const cycle = findDependencyCycle(nodes, module.module_key);
        if (cycle) { errors.push(dependencyCycleMessage(nodes, cycle)); break; }
    }

    return errors.length > 0 ? errors : null;
  }, [selectedCourseFull, allModules]);

  const saveCourse = useCallback(async () => {
    if (!selectedCourseFull || courseSaveInFlight.current || mode === 'view') return false;
    setSelectedCourseError(null);
    const errors = validateCourse();
    toastRef.current?.clear();
    if (errors) {
        const course = selectedCourseFull;
        const field = !course.name?.trim() ? 'course-name' : !course.description?.trim() ? 'course-description'
            : course.price == null || !Number.isFinite(course.price) || course.price <= 0 ? 'course-price'
            : !course.modules?.length ? 'course-modules' : undefined;
        setSaveErrorLocation(current => ({ section: field || course.course_prerequisites.some(p => p.course_key === course.course_key) ? 'general' : 'modules', field, request: (current?.request ?? 0) + 1 }));
        toastRef.current?.show({ severity: 'error', summary: 'Validation failed', detail: errors.join(' '), life: 10000 });
        return false;
    }
    const sections = courseDraft.current?.();
    if (sections === null) return false;
    const payload: CourseDto = { ...selectedCourseFull, ...sections,
        certificate_enabled: selectedCourseFull.certificate_enabled ?? true,
        cert_description: selectedCourseFull.cert_description ?? null, cert_prefix: selectedCourseFull.cert_prefix ?? null };
    courseSaveInFlight.current = true;
    setSelectedCourseLoading(true);
    try {
        const cycle = await checkDependencyCycle({ key: payload.course_key, name: payload.name,
            prerequisites: payload.course_prerequisites.map(item => item.course_key) }, async key => {
                const course = await api.getCourse(key);
                return course ? { key: course.course_key, name: course.name,
                    prerequisites: course.course_prerequisites.map(item => item.course_key) } : null;
            });
        if (cycle) {
            setSelectedCourseError(cycle);
            setSaveErrorLocation(current => ({ section: 'general', request: (current?.request ?? 0) + 1 }));
            return false;
        }
        const { course_key: key, ...newCourse } = payload;
        const result = mode === 'create' ? await api.createCourse(newCourse as CourseDto) : await api.updateCourse(key, payload);
        if (!result || 'message' in result || !result.course_key) {
            throw new Error(result && 'message' in result ? result.message : 'The server did not confirm the complete course save. Your draft has been kept.');
        }
        guard.markSaved(result);
        setSelectedCourseFull(result);
        setOriginalCourseFull(result);
        setAllCourses(previous => mode === 'create' ? [...previous, result] : previous.map(course => course.course_key === result.course_key ? result : course));
        if (mode === 'create') afterSave(() => setSelectedCourseKey(result.course_key));
        setMode('view');
        setSaveErrorLocation(null);
        toastRef.current?.show({ severity: 'success', summary: 'Course saved', life: 4000 });
        return true;
    } catch (error: any) {
        setSelectedCourseError(error.response?.data?.message ?? error.message ?? 'Could not save the course. Your draft has been kept.');
        const section = error.response?.data?.section;
        if (section) setSaveErrorLocation(current => ({ section, field: error.response?.data?.field, request: (current?.request ?? 0) + 1 }));
        return false;
    } finally {
        courseSaveInFlight.current = false;
        setSelectedCourseLoading(false);
    }
    // guard is a stable draft registration; its save callback is this function.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selectedCourseFull, validateCourse, api, afterSave, setSelectedCourseKey]);

  const cancelEditing = useCallback(() => {
    setSaveErrorLocation(null);
    setSelectedCourseError(null);
    if (mode === "create") {
        // Cancel creation → go back to view mode with no course selected
        if (originalCourseFull) {
            setSelectedCourseFull(structuredClone(originalCourseFull));
        } else {
            setSelectedCourseFull(null);
        }
        setOriginalCourseFull(null);
        setSelectedCourseKey(originalCourseFull?.course_key ?? null);
        setMode("view");
        return;
    }

    if (mode === "edit") {
        // Revert to last saved version
        if (originalCourseFull) {
            setSelectedCourseFull(structuredClone(originalCourseFull));
        }
        setMode("view");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, originalCourseFull, setSelectedCourseKey]);

  const deleteCourse = useCallback(async (course_key: string) => {
    try {
      const deleteMessage: MessageDto = await api.deleteCourse(course_key);
      if (deleteMessage && deleteMessage.message === 'OK') {
        setSelectedCourseKey(null);
        setAllCourses(prev => prev.filter(c => c.course_key !== course_key));
        setSelectedCourseFull(null);
        setOriginalCourseFull(null);
        setMode("view");
        toastRef.current?.clear();
        toastRef.current?.show({
            severity: "success",
            summary: "Course deleted",
            detail: `The course has been removed.`,
            life: 4000
        });
      } else {
        const message: string = (deleteMessage && deleteMessage.message)
            ? deleteMessage.message
            : 'The course might not have been deleted from the server propertly. Refresh the page to see if it was actually deleted.';
        setSelectedCourseError(message);
      }
    } catch (error: any) {
        const message: string = error.response?.data?.message ?? 'Failed to save course';
        setSelectedCourseError(message);
        setSelectedCourseFull(null);
    } finally {
        setSelectedCourseLoading(false);
    }
  }, [api, setSelectedCourseKey, toastRef]);

  const guard = useUnsavedDraft({ active: mode !== 'view' && selectedCourseFull !== null,
    value: selectedCourseFull, parameter: 'course', save: saveCourse, discard: cancelEditing });

  return (
    <CourseManagementDataContext.Provider
      value={{
        toastRef,

        allCourses,
        allCoursesLoading,
        allCoursesError,
        loadAllCourses,

        allModules,
        allModulesLoading,
        allModulesError,
        loadAllModules,

        allUsers,
        allUsersLoading,
        allUsersError,
        loadAllUsers,

        selectedCourseKey,
        selectedCourseFull,
        selectedCourseLoading,
        selectedCourseError,

        mode,
        selectCourse: (courseKey) => requestAction(() => selectCourse(courseKey)),
        startEditCourse: (courseKey) => requestAction(() => startEditCourse(courseKey)),
        startCreateCourse: () => requestAction(() => startCreateCourse()),
        initNewCourse,

        // new update functions
        updateCourseField,
        updateCoursePrerequisites,
        updateCourseModules,
        updateModulePrerequisites,
        updateCourseUsers,
        addUsersToCourse,
        removeUserFromCourse,
        validateCourse,
        saveCourse,
        registerCourseDraft,
        originalCourseFull,
        saveErrorLocation,
        deleteCourse,
        cancelEditing: () => withoutWarning(cancelEditing),
      }}
    >
      {children}
      <AppToastOutlet toastRef={toastRef} />
    </CourseManagementDataContext.Provider>
  );
}
